import { tenantCapabilityCatalogDefault, tenantCapabilityWithFallback } from "../../app/auth/tenantCapabilitiesRead";
import type { ChannelFlags, MessagingChannel } from "../../app/integrations/useChannelAvailability";
import { rowAccessPermissions } from "../../app/permissions/permissionRows";
import type { LocalizedText } from "../../app/i18n/i18n";

export type SettingsCatalogItem = {
  titleKey: LocalizedText;
  descriptionKey: LocalizedText;
  path: string;
  iconKey: string;
  requiredPermissions?: string[]; // Required permissions to access this setting
  /** If set, the item is only shown when the role matches. */
  requiredRole?: "CompanyAdmin";
  /** Optional capability key for feature gating / lock badge. */
  capabilityKey?: string;
};

export type SettingsCatalogGroup = {
  /** Slug der Gruppenseite unter /settings/group/:groupKey. */
  key: string;
  titleKey: LocalizedText;
  /** Beschriftung des Zurück-Links auf den Detailseiten dieser Gruppe. */
  backLabelKey: LocalizedText;
  items: SettingsCatalogItem[];
};

export const settingsCatalogGroups: SettingsCatalogGroup[] = [
  {
    key: "organisation",
    backLabelKey: "Back to Organisation & Users",
    titleKey: "Organization & Users",
    items: [
      {
        titleKey: "Master data",
        iconKey: "SettingOutlined",
        descriptionKey: "Company & Studios",
        path: "/settings/master-data",
        requiredPermissions: rowAccessPermissions("masterData")
      },
      {
        titleKey: "Legal texts",
        iconKey: "FileTextOutlined",
        descriptionKey: "SettingsCards.settings.legal-text-settings",
        path: "/settings/legal-text-settings",
        requiredPermissions: rowAccessPermissions("legalTexts")
      },
      {
        titleKey: "Users",
        iconKey: "UserOutlined",
        descriptionKey: "PIN, login & studio assignment",
        path: "/settings/users",
        requiredPermissions: rowAccessPermissions("users")
      },
      {
        titleKey: "Roles",
        iconKey: "SafetyCertificateOutlined",
        descriptionKey: "Permissions & roles",
        path: "/settings/user-roles",
        requiredPermissions: rowAccessPermissions("roles")
      }
    ]
  },
  {
    key: "members",
    backLabelKey: "Back to Member management",
    titleKey: "Member management",
    items: [
      {
        titleKey: "Member management",
        iconKey: "IdcardOutlined",
        descriptionKey: "SettingsCards.settings.person-defaults",
        path: "/settings/person-defaults",
        requiredPermissions: rowAccessPermissions("personDefaults")
      },
      {
        titleKey: "Check-in view",
        iconKey: "DesktopOutlined",
        descriptionKey: "SettingsCards.settings.checkin-view",
        path: "/settings/checkin-view",
        requiredPermissions: rowAccessPermissions("checkinView")
      }
    ]
  },
  {
    key: "products",
    backLabelKey: "Back to Products & Contracts",
    titleKey: "Products & Contracts",
    items: [
      {
        titleKey: "Contract packages",
        iconKey: "FileDoneOutlined",
        descriptionKey: "SettingsCards.settings.templates",
        path: "/settings/templates",
        requiredPermissions: rowAccessPermissions("contractPackages")
      },
      {
        titleKey: "Contract components",
        iconKey: "AppstoreAddOutlined",
        descriptionKey: "Components for contract packages",
        path: "/settings/contract-templates",
        requiredPermissions: rowAccessPermissions("contractComponents")
      },
      {
        titleKey: "Payment plans",
        iconKey: "ReconciliationOutlined",
        descriptionKey: "SettingsCards.settings.payment-plans",
        path: "/settings/payment-plans",
        requiredPermissions: rowAccessPermissions("paymentPlans")
      },
      {
        titleKey: "Value accounts",
        iconKey: "WalletOutlined",
        descriptionKey: "Value account products",
        path: "/settings/wallet-products",
        requiredPermissions: rowAccessPermissions("walletProducts")
      },
      {
        titleKey: "Articles",
        iconKey: "ShoppingOutlined",
        descriptionKey: "SettingsCards.settings.articles",
        path: "/settings/articles",
        requiredPermissions: rowAccessPermissions("articles")
      },
      {
        titleKey: "Rest periods",
        iconKey: "PauseCircleOutlined",
        descriptionKey: "Rest period models and reasons",
        path: "/settings/rest-period-templates",
        requiredPermissions: rowAccessPermissions("restPeriodModels")
      },
      {
        titleKey: "Cancellation models",
        iconKey: "FileProtectOutlined",
        descriptionKey: "Cancellation models and reasons",
        path: "/settings/cancellation-models",
        requiredPermissions: rowAccessPermissions("cancellationModels")
      },
      {
        titleKey: "PriceAdjustments",
        iconKey: "PercentageOutlined",
        descriptionKey: "PriceAdjustment templates for contracts",
        path: "/settings/price-adjustments",
        requiredPermissions: rowAccessPermissions("priceAdjustmentModels")
      },
      {
        titleKey: "Option groups",
        iconKey: "AppstoreAddOutlined",
        descriptionKey: "SettingsCards.settings.contract-option-groups",
        path: "/settings/contract-option-groups",
        requiredPermissions: rowAccessPermissions("contractOptionGroups")
      },
      {
        titleKey: "Sales promotions",
        iconKey: "TagsOutlined",
        descriptionKey: "SettingsCards.settings.sales-promotions",
        path: "/settings/sales-promotions",
        requiredPermissions: rowAccessPermissions("salesPromotions")
      },
      {
        titleKey: "Promotion templates",
        iconKey: "GiftOutlined",
        descriptionKey: "SettingsCards.settings.member-promotion-templates",
        path: "/settings/member-promotion-templates",
        requiredPermissions: rowAccessPermissions("memberPromotionTemplates")
      }
    ]
  },
  {
    key: "payments",
    backLabelKey: "Back to Payments & Billing",
    titleKey: "Payments & Billing",
    items: [
      {
        titleKey: "Accounting",
        iconKey: "FileTextOutlined",
        descriptionKey: "Accounts, assignments & cost centers",
        path: "/settings/accounting",
        requiredPermissions: rowAccessPermissions("accounting")
      },
      {
        titleKey: "Bank accounts",
        iconKey: "BankOutlined",
        descriptionKey: "Bank details, direct debit groups & mandate reference",
        path: "/settings/bank",
        requiredPermissions: rowAccessPermissions("bankAccounts")
      },
      {
        titleKey: "Credit account",
        iconKey: "DollarCircleOutlined",
        descriptionKey: "Default account for credit payments",
        path: "/settings/default-wallet-payment",
        requiredPermissions: rowAccessPermissions("defaultWalletPayment")
      },
      {
        titleKey: "Payment methods",
        iconKey: "CreditCardOutlined",
        descriptionKey: "SettingsCards.settings.payment-methods",
        path: "/settings/payment-methods",
        requiredPermissions: rowAccessPermissions("paymentMethods")
      },
      {
        titleKey: "Cash registers",
        iconKey: "ShopOutlined",
        descriptionKey: "SettingsCards.settings.cash-registers",
        path: "/settings/cash-registers",
        requiredPermissions: rowAccessPermissions("accounting")
      },
      {
        titleKey: "Reversal reasons",
        iconKey: "CloseCircleOutlined",
        descriptionKey: "Reasons for payment and contribution cancellations",
        path: "/settings/payment-cancellation-reasons",
        requiredPermissions: rowAccessPermissions("cancellationReasonsFinance")
      },
      {
        titleKey: "Dunning",
        iconKey: "AlertOutlined",
        descriptionKey: "Reminder levels & collection agencies",
        path: "/settings/debt-management",
        requiredPermissions: rowAccessPermissions("debtManagement")
      }
    ]
  },
  {
    key: "leads",
    backLabelKey: "Back to Leads",
    titleKey: "Leads",
    items: [
      {
        titleKey: "Sources",
        iconKey: "BranchesOutlined",
        descriptionKey: "Manage lead sources",
        path: "/settings/lead-sources",
        requiredPermissions: rowAccessPermissions("leadSources")
      },
      {
        titleKey: "Pipelines & phases",
        iconKey: "ApartmentOutlined",
        descriptionKey: "Sales pipelines and phases",
        path: "/settings/lead-pipelines",
        requiredPermissions: rowAccessPermissions("leadPipelines")
      },
      {
        titleKey: "Lead-Widgets",
        iconKey: "BgColorsOutlined",
        descriptionKey: "Manage online lead widgets and design presets",
        path: "/settings/lead-widgets",
        requiredPermissions: rowAccessPermissions("leadWidgets")
      },
    ]
  },
  {
    // Vertragsabschluss ist ein eigener Bereich und hängt nicht am Lead-Manager (Audit F24/D17).
    key: "contractSigning",
    backLabelKey: "Back to contract signing settings",
    titleKey: "Close contract",
    items: [
      {
        titleKey: "Contract signing widgets",
        iconKey: "FileTextOutlined",
        descriptionKey: "Public widgets for online contract signing",
        path: "/settings/contract-signing-widgets",
        requiredPermissions: rowAccessPermissions("contractSigningWidgets")
      }
    ]
  },
  {
    key: "memberCare",
    backLabelKey: "Back to member care settings",
    titleKey: "Member care settings",
    items: [
      {
        titleKey: "Pipelines & phases",
        iconKey: "ApartmentOutlined",
        descriptionKey: "Member care pipelines description",
        path: "/settings/member-care-pipelines",
        requiredPermissions: rowAccessPermissions("memberCarePipelines")
      }
    ]
  },
  {
    key: "communication",
    backLabelKey: "Back to Communication",
    titleKey: "Communication",
    items: [
      {
        titleKey: "Letter and email templates",
        iconKey: "MessageOutlined",
        descriptionKey: "SettingsCards.settings.letter-email-templates",
        path: "/settings/letter-email-templates",
        requiredPermissions: rowAccessPermissions("templates")
      },
      {
        titleKey: "Categories",
        iconKey: "TagsOutlined",
        descriptionKey: "Default templates & categories",
        path: "/settings/template-categories",
        requiredPermissions: rowAccessPermissions("templates")
      },
      {
        titleKey: "WhatsApp templates",
        iconKey: "MessageOutlined",
        descriptionKey: "SettingsCards.settings.whatsapp-templates",
        path: "/settings/whatsapp-templates",
        requiredPermissions: rowAccessPermissions("templates")
      },
      {
        titleKey: "SMS templates",
        iconKey: "MessageOutlined",
        descriptionKey: "SettingsCards.settings.sms-templates",
        path: "/settings/sms-templates",
        requiredPermissions: rowAccessPermissions("templates")
      },
      {
        titleKey: "Document appearance",
        iconKey: "LayoutOutlined",
        descriptionKey: "SettingsCards.settings.document-appearance",
        path: "/settings/document-appearance",
        requiredPermissions: rowAccessPermissions("documentAppearance")
      },
      {
        titleKey: "Fonts",
        iconKey: "FontSizeOutlined",
        descriptionKey: "SettingsCards.settings.fonts",
        path: "/settings/fonts",
        requiredPermissions: rowAccessPermissions("documentAppearance")
      },
      {
        titleKey: "Email",
        iconKey: "MailOutlined",
        descriptionKey: "Accounts & spooler",
        path: "/settings/email",
        requiredPermissions: rowAccessPermissions("emailSettings")
      },
    ]
  },
  {
    key: "calendar",
    backLabelKey: "Back to Calendar & Appointments",
    titleKey: "Calendar & Appointments",
    items: [
      {
        titleKey: "Calendar",
        iconKey: "ClockCircleOutlined",
        descriptionKey: "Times, settings & holidays",
        path: "/settings/appointment-configuration",
        requiredPermissions: rowAccessPermissions("appointmentConfig")
      },
      {
        titleKey: "Appointment types",
        iconKey: "TagsOutlined",
        descriptionKey: "Types of appointments",
        path: "/settings/appointment-types",
        requiredPermissions: rowAccessPermissions("appointmentTypes")
      },
      {
        titleKey: "Resources",
        iconKey: "AppstoreOutlined",
        descriptionKey: "Persons, pools, equipment & rooms",
        path: "/settings/resources",
        requiredPermissions: rowAccessPermissions("resources")
      },
      {
        titleKey: "Unavailabilities",
        iconKey: "CalendarOutlined",
        descriptionKey: "SettingsCards.settings.resource-unavailabilities",
        path: "/settings/resource-unavailabilities",
        requiredPermissions: rowAccessPermissions("resourceUnavailabilities")
      },
      {
        titleKey: "Availabilities",
        iconKey: "CalendarOutlined",
        descriptionKey: "Weekly planning & templates",
        path: "/settings/availabilities",
        requiredPermissions: rowAccessPermissions("availabilities")
      },
      {
        titleKey: "Appointment outcomes",
        iconKey: "CheckCircleOutlined",
        descriptionKey: "Result/status templates",
        path: "/settings/appointment-outcomes",
        requiredPermissions: rowAccessPermissions("appointmentOutcomes")
      },
      {
        titleKey: "Booking widgets",
        iconKey: "GlobalOutlined",
        descriptionKey: "SettingsCards.settings.booking-widgets",
        path: "/settings/booking-widgets",
        requiredPermissions: rowAccessPermissions("bookingWidgets")
      },
      {
        titleKey: "Appointment notifications",
        iconKey: "BellOutlined",
        descriptionKey: "SettingsCards.settings.appointment-notifications",
        path: "/settings/appointment-notifications",
        requiredPermissions: rowAccessPermissions("appointmentNotifications")
      }
    ]
  },
  {
    key: "staff",
    backLabelKey: "Back to Staff",
    titleKey: "Staff",
    items: [
      {
        titleKey: "Activity types",
        iconKey: "ScheduleOutlined",
        descriptionKey: "SettingsCards.settings.staff-activity-types",
        path: "/settings/staff-activity-types",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Absence types",
        iconKey: "CalendarOutlined",
        descriptionKey: "SettingsCards.settings.staff-absence-types",
        path: "/settings/staff-absence-types",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Employee master data",
        iconKey: "TeamOutlined",
        descriptionKey: "SettingsCards.settings.staff-profiles",
        path: "/settings/staff-profiles",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Staff schedule settings",
        iconKey: "ScheduleOutlined",
        descriptionKey: "Time grid, working days and booking fallback",
        path: "/settings/staff-schedule-settings",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Time tracking and HR",
        iconKey: "ClockCircleOutlined",
        descriptionKey: "SettingsCards.settings.staff-hr",
        path: "/settings/staff-hr",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Minimum wage",
        iconKey: "DollarOutlined",
        descriptionKey: "Minimum wage rates",
        path: "/settings/staff-minimum-wage",
        requiredPermissions: rowAccessPermissions("staff.settings")
      },
      {
        titleKey: "Planning rules",
        iconKey: "ScheduleOutlined",
        descriptionKey: "Optional rules for the suggested week",
        path: "/settings/staff-planning-rules",
        requiredPermissions: rowAccessPermissions("staff.schedule")
      },
      {
        titleKey: "Week plan links",
        iconKey: "LinkOutlined",
        descriptionKey: "SettingsCards.settings.staff-week-plan-links",
        path: "/settings/staff-week-plan-links",
        requiredPermissions: rowAccessPermissions("staff.settings")
      }
    ]
  },
  {
    key: "access",
    backLabelKey: "Back to Access & Devices",
    titleKey: "Access & Devices",
    items: [
      {
        titleKey: "Reader devices",
        iconKey: "KeyOutlined",
        descriptionKey: "Manage access reader devices",
        path: "/settings/reader-devices",
        requiredPermissions: rowAccessPermissions("readerDevices")
      },
      {
        titleKey: "Studio app",
        iconKey: "MobileOutlined",
        descriptionKey: "SettingsCards.settings.studio-app",
        path: "/settings/studio-app",
        requiredPermissions: rowAccessPermissions("memberApp")
      }
    ]
  },
  {
    key: "system",
    backLabelKey: "Back to System",
    titleKey: "System",
    items: [
      {
        titleKey: "Units",
        iconKey: "GlobalOutlined",
        descriptionKey: "Date, time, timezone, currency",
        path: "/settings/units",
        requiredPermissions: rowAccessPermissions("units")
      },
      {
        titleKey: "Navigation.general",
        iconKey: "DesktopOutlined",
        descriptionKey: "SettingsCards.settings.default-theme",
        path: "/settings/general",
        requiredPermissions: rowAccessPermissions("defaultTheme")
      },
      {
        titleKey: "User templates",
        iconKey: "BarChartOutlined",
        descriptionKey: "Manage report & table templates",
        path: "/settings/user-preferences",
        requiredPermissions: rowAccessPermissions("userPreferences")
      },
      {
        titleKey: "Integrations",
        iconKey: "GlobalOutlined",
        descriptionKey: "SettingsCards.settings.integrations",
        path: "/settings/integrations",
        requiredPermissions: rowAccessPermissions("integrations")
      },
      {
        titleKey: "Delete my data",
        iconKey: "DeleteOutlined",
        descriptionKey: "SettingsCards.settings.tenant-deletion",
        path: "/settings/tenant-deletion",
        requiredRole: "CompanyAdmin"
      },
    ]
  },
];

/**
 * Mandanten-Capability, die ein einzelner Einstellungsbereich voraussetzt. Einzige Quelle für
 * Übersichtsseite und Gruppenseite — beide filtern damit dieselbe Menge.
 */
export const settingsCapabilityByPath: Record<string, string> = {
  "/settings/articles": "articleSales",
  "/settings/studio-app": "memberApp",
  "/settings/lead-sources": "leadManager",
  "/settings/lead-pipelines": "leadManager",
  "/settings/lead-widgets": "leadManager",
  "/settings/member-care-pipelines": "memberCare",
  "/settings/appointment-configuration": "calendar",
  "/settings/appointment-types": "calendar",
  "/settings/resources": "calendar",
  "/settings/resource-unavailabilities": "calendar",
  "/settings/availabilities": "calendar",
  "/settings/appointment-outcomes": "calendar",
  "/settings/appointment-notifications": "calendar",
  "/settings/staff-activity-types": "staffScheduling",
  "/settings/staff-absence-types": "staffScheduling",
  "/settings/staff-profiles": "staffScheduling",
  "/settings/staff-schedule-settings": "staffScheduling",
  "/settings/staff-hr": "staffScheduling",
  "/settings/staff-minimum-wage": "staffScheduling",
  "/settings/staff-week-plan-links": "staffScheduling",
  "/settings/staff-planning-rules": "staffScheduling",
  "/settings/booking-widgets": "bookingWidgets",
  "/settings/contract-signing-widgets": "contractSigningWidgets",
  "/settings/debt-management": "debtCollection",
  // Brief-/E-Mail-Texte, Dokumentgestaltung und Schriften brauchen auch Vertragsabläufe ohne
  // Kommunikationsmodul; SMS/WhatsApp hängen am Kanal (settingsChannelByPath).
  "/settings/email": "communication",
  "/settings/member-promotion-templates": "memberPromotions"
};

const SettingsCapability = { Leads: "leadManager", MemberCare: "memberCare", Calendar: "calendar", Staff: "staffScheduling", ContractSigning: "contractSigningWidgets" } as const;
const PAYMENT_INTERFACES_PATH = "/settings/payment-interfaces";

/** Capability, ohne die eine ganze Gruppe nicht zugänglich ist. */
export const settingsCapabilityByGroupKey: Record<string, string> = {
  leads: SettingsCapability.Leads,
  memberCare: SettingsCapability.MemberCare,
  calendar: SettingsCapability.Calendar,
  staff: SettingsCapability.Staff,
  contractSigning: SettingsCapability.ContractSigning
};

/**
 * Ob die Capability einer Gruppe freigeschaltet ist. Einzige Prüfung für Übersicht und Gruppenseite;
 * Kalender und Lead-Manager kennen zusätzlich die alten Modulschalter aus `/auth/me`.
 */
export function settingsGroupCapabilityEnabled(
  groupKey: string,
  auth: { capabilities?: unknown; moduleCalendarEnabled?: boolean; moduleLeadManagerEnabled?: boolean } | null | undefined
): boolean {
  const capability = settingsCapabilityByGroupKey[groupKey];
  if (!capability) return true;
  if (tenantCapabilityWithFallback(auth?.capabilities, capability, tenantCapabilityCatalogDefault(capability))) return true;
  if (capability === SettingsCapability.Calendar) return auth?.moduleCalendarEnabled ?? false;
  if (capability === SettingsCapability.Leads) return auth?.moduleLeadManagerEnabled ?? false;
  return false;
}

/** Rechte einer Einstellungsseite — einzige Quelle für Kachel, Menügruppe und Router-Guard. */
export function settingsPagePermissions(path: string): string[] {
  const item = settingsCatalogGroups.flatMap((g) => g.items).find((i) => i.path === path);
  if (!item) throw new Error(`Kein Einstellungs-Katalogeintrag für ${path}`);
  return item.requiredPermissions ?? [];
}

/** Alle Rechte, die irgendeine Einstellungsseite öffnen — für `/settings` und die Gruppenseite. */
export const allSettingsPermissions: string[] = Array.from(new Set(settingsCatalogGroups.flatMap((g) => g.items.flatMap((i) => i.requiredPermissions ?? []))));

/** Seiten, die es nur mit konfigurierter Integration gibt (Brief/SMS/WhatsApp, `useChannelAvailability`). */
export const settingsChannelByPath: Record<string, MessagingChannel> = {
  "/settings/whatsapp-templates": "whatsApp",
  "/settings/sms-templates": "sms"
};

export type SettingsVisibilityContext = {
  capabilities: unknown;
  role: string;
  isOwnerOrAdmin?: boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  /** Versendbare Kanäle des Mandanten — Seiten aus `settingsChannelByPath` ohne Kanal bleiben verborgen. */
  channels: ChannelFlags;
};

/** Sichtbare Einträge einer Gruppe: Capability, Rolle und Berechtigungen. */
export function filterSettingsItems(
  items: SettingsCatalogItem[] | undefined,
  ctx: SettingsVisibilityContext
): SettingsCatalogItem[] {
  return (items || [])
    .filter((item) => {
      const requiredCapability = item.capabilityKey ?? settingsCapabilityByPath[item.path];
      if (requiredCapability) {
        const isEnabled = tenantCapabilityWithFallback(
          ctx.capabilities,
          requiredCapability,
          tenantCapabilityCatalogDefault(requiredCapability)
        );
        if (!isEnabled && !ctx.isOwnerOrAdmin) return false;
      }
      const requiredChannel = settingsChannelByPath[item.path];
      if (requiredChannel && !ctx.channels[requiredChannel]) return false;
      if (item.requiredRole && ctx.role !== item.requiredRole) return false;
      if (!item.requiredPermissions || item.requiredPermissions.length === 0) return true;
      return ctx.hasAnyPermission(...item.requiredPermissions);
    })
    .map((item) => {
      const cap = item.capabilityKey ?? settingsCapabilityByPath[item.path];
      return cap ? { ...item, capabilityKey: cap } : item;
    });
}

const titleByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const g of settingsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path) continue;
      // Use translation keys so tr() works in AppLayout.
      if (typeof item.titleKey === "string") map[String(item.path)] = item.titleKey;
    }
  }
  return map;
})();

const groupByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const g of settingsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path) continue;
      if (typeof g.titleKey === "string") map[String(item.path)] = g.titleKey;
    }
  }
  return map;
})();

const iconKeyByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const g of settingsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path || !item.iconKey) continue;
      map[String(item.path)] = item.iconKey;
    }
  }
  return map;
})();

export function settingsTitleForPath(pathname: string): string | null {
  if (pathname === PAYMENT_INTERFACES_PATH) return "merchantPayments.title";
  const p = String(pathname || "");
  return titleByPath[p] || null;
}

/** Symbol-Schlüssel der Einstellungsseite — damit Pins dasselbe Symbol tragen wie die Kachel. */
export function settingsIconKeyForPath(pathname: string): string | null {
  const p = String(pathname || "");
  return iconKeyByPath[p] || null;
}

export function settingsGroupForPath(pathname: string): string | null {
  const p = String(pathname || "");
  return groupByPath[p] || null;
}

const backGroupByPath: Record<string, { key: string; labelKey: LocalizedText }> = (() => {
  const map: Record<string, { key: string; labelKey: LocalizedText }> = {};
  for (const g of settingsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path) continue;
      map[String(item.path)] = { key: g.key, labelKey: g.backLabelKey };
    }
  }
  return map;
})();

const backGroupPathsByLength: string[] = Object.keys(backGroupByPath).sort((a, b) => b.length - a.length);

/**
 * Gruppen-Zuordnung für den Zurück-Link einer Einstellungsseite. Wird direkt aus dem Katalog
 * abgeleitet, damit jeder neue Katalogeintrag den Zurück-Link automatisch bekommt — eine
 * parallele Pfadliste ist genau daran wiederholt veraltet.
 * Untersegmente (z. B. `/settings/templates/:id`) erben die Gruppe ihres Katalogpfads.
 */
export function settingsBackGroupForPath(pathname: string): { key: string; labelKey: LocalizedText } | null {
  if (pathname === PAYMENT_INTERFACES_PATH) return backGroupByPath["/settings/integrations"] ?? null;
  const p = String(pathname || "");
  const exact = backGroupByPath[p];
  if (exact) return exact;
  const prefix = backGroupPathsByLength.find((candidate) => p.startsWith(candidate + "/"));
  return prefix ? backGroupByPath[prefix] : null;
}

/** Liste, unter deren Pfad ein Eintrag (Detail, Neu, Duplikat) liegt — Ziel des Zurück-Links statt der Gruppe. */
export function settingsListForEntryPath(pathname: string): { path: string; titleKey: string } | null {
  const p = String(pathname || "");
  if (backGroupByPath[p]) return null;
  const list = backGroupPathsByLength.find((candidate) => p.startsWith(candidate + "/"));
  const titleKey = list ? settingsTitleForPath(list) : null;
  return list && titleKey ? { path: list, titleKey } : null;
}

export function settingsFullTitleForPath(pathname: string): string | null {
  const group = settingsGroupForPath(pathname);
  const title = settingsTitleForPath(pathname);
  if (!group || !title) return title;
  return `${group}: ${title}`;
}

/** Calendar settings paths for the layout studio selector. */
export const CALENDAR_GROUP_PATHS: string[] = [
  "/settings/appointment-configuration",
  "/settings/appointment-types",
  "/settings/resources",
  "/settings/resource-unavailabilities",
  "/settings/availabilities",
  "/settings/appointment-outcomes",
  "/settings/booking-widgets",
  "/settings/appointment-notifications"
];

export function isCalendarGroupPath(pathname: string): boolean {
  return CALENDAR_GROUP_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}
